'use client';
import React, { useState } from 'react';
import { FileText, Download, Calendar, Plus, Clock } from 'lucide-react';
import { toast } from 'sonner';

const reports = [
  {
    id: 'rpt-001',
    name: 'Executive Weekly Summary',
    frequency: 'Weekly',
    format: 'PDF',
    nextRun: 'May 19, 2026',
    recipients: 4,
    status: 'active' as const,
  },
  {
    id: 'rpt-002',
    name: 'Sales Pipeline Export',
    frequency: 'Daily',
    format: 'CSV',
    nextRun: 'May 15, 2026',
    recipients: 8,
    status: 'active' as const,
  },
  {
    id: 'rpt-003',
    name: 'Monthly KPI Board Pack',
    frequency: 'Monthly',
    format: 'PDF',
    nextRun: 'Jun 01, 2026',
    recipients: 12,
    status: 'active' as const,
  },
  {
    id: 'rpt-004',
    name: 'Inventory Risk Report',
    frequency: 'Weekly',
    format: 'CSV',
    nextRun: 'May 19, 2026',
    recipients: 3,
    status: 'paused' as const,
  },
];

export default function ScheduledReports() {
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const handleExport = (report: typeof reports[0]) => {
    setLoadingId(report.id);
    setTimeout(() => {
      setLoadingId(null);
      toast.success(`${report.name} exported as ${report.format}`);
    }, 1400);
  };

  return (
    <div className="card-surface p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">Scheduled Reports</h2>
          <p className="text-sm text-muted-foreground">Auto-delivery exports</p>
        </div>
        <button className="flex items-center gap-1.5 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-primary/90 transition-colors btn-press">
          <Plus size={13} />
          Add
        </button>
      </div>

      <div className="space-y-2.5">
        {reports.map((report) => (
          <div
            key={report.id}
            className={`rounded-lg p-3 border transition-all duration-150 hover:bg-muted/30 ${
              report.status === 'paused' ? 'border-border opacity-60' : 'border-border'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2 min-w-0">
                <FileText size={14} className={`mt-0.5 flex-shrink-0 ${
                  report.format === 'PDF' ? 'text-negative' : 'text-positive'
                }`} />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-foreground truncate">{report.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock size={10} />
                      {report.frequency}
                    </span>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className={`text-xs font-semibold ${
                      report.format === 'PDF' ? 'text-negative' : 'text-positive'
                    }`}>
                      {report.format}
                    </span>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className="text-xs text-muted-foreground">{report.recipients} recipients</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleExport(report)}
                disabled={loadingId === report.id}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-semibold transition-colors disabled:opacity-50 flex-shrink-0"
                aria-label={`Export ${report.name}`}
              >
                {loadingId === report.id ? (
                  <span className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download size={13} />
                )}
                {loadingId === report.id ? 'Exporting...' : 'Export'}
              </button>
            </div>
            <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar size={10} />
              Next: {report.nextRun}
              {report.status === 'paused' && (
                <span className="ml-auto label-caps bg-muted text-muted-foreground rounded px-1.5 py-0.5">
                  Paused
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}